import React, { useState, useMemo, useRef, useEffect } from 'react';
import zipcelx from 'zipcelx';
import { cloneDeep } from 'lodash';
import Highcharts, { Options as HighchartsOptions } from 'highcharts';
import '~/shared/utils/highchartsAllowList';
import HCAccessibility from 'highcharts/modules/accessibility';
import HighchartsReact from 'highcharts-react-official';
import HCExporting from 'highcharts/modules/exporting';
import HCOfflineExporting from 'highcharts/modules/offline-exporting';
import HCExportData from 'highcharts/modules/export-data';
import HCNoData from 'highcharts/modules/no-data-to-display';
import styled from 'styled-components';
import reactGA from 'react-ga';
import CustomEvents from 'highcharts-custom-events';
import moment from 'moment';
import { highchartsTooltipCfg } from '~/shared/config/highchartsCfg';
import { hex2rgba } from '../../../utils/colors/hex2rgba';
import { formatTooltipHtml } from '~/shared/utils';
import { buildChartExportFileName } from '~/shared/utils/buildChartExportFileName';

HCExporting(Highcharts);
HCExportData(Highcharts);
HCNoData(Highcharts);
HCOfflineExporting(Highcharts);
CustomEvents(Highcharts as any);
HCAccessibility(Highcharts);

(function (H) {
  H.Chart.prototype.downloadXLS = function () {
    const div = document.createElement('div');
    let xlsxRows = [];
    div.style.display = 'none';
    document.body.appendChild(div);
    const rows = this.getDataRows(true);
    xlsxRows = H.map(rows.slice(1), function (row: any) {
      return H.map(row, function (column: any) {
        let tempColumn = column;
        if (typeof column === 'string' && column?.includes('00:00:00')) {
          tempColumn = moment(column).format('MM/DD/YYYY');
        }
        return {
          type: typeof column === 'number' ? 'number' : 'string',
          value: tempColumn,
        };
      });
    });

    const name = this.options.exporting!.filename;
    zipcelx({
      filename: name as unknown as string,
      sheet: {
        data: xlsxRows,
      },
    });
  };
})(Highcharts);

const EMPTY_OPTIONS: HighchartsOptions = { series: [] };

export interface IStackBarChart {
  axisXLabels: string[];
  axisYLabels: string[];
  values: { XLabel: string; YLabel: string; Value: number }[];
  matrix: number[][];
  data?: any;
}

interface ISeriesObj {
  name: string;
  data: string[];
  color: string | undefined;
}

const Root = styled.div<{ $gridheight: number }>`
  height: ${({ $gridheight }) => ($gridheight ? $gridheight * 115 : 400)}px;
  width: 100%;
  position: relative;
  margin-top: 10px;
`;

const HighchartContainer = styled.div`
  height: 100%;
  width: 100%;
  box-sizing: border-box;
  position: absolute;
  border: solid 1px var(--border-chart);
  background: var(--background-chart);
  padding: 10px;
  .highcharts-tooltip span {
    z-index: 9999 !important;
  }
  .tooltip {
    padding: 2px;
    background-color: var(--background-chart-tooltip);
    opacity: 1;
    z-index: 9999 !important;
  }
  .highcharts-button-box {
    x: 2;
    y: 1;
    rx: 16;
    ry: 16;
    &:hover {
      fill: #d4e5ff !important;
    }
  }
  .highcharts-button-symbol {
    width: 3.5px !important;
    fill: #4c5e72 !important;
    stroke: transparent !important;
    pointer-events: none;
  }
  .highcharts-indicator-menu {
    max-width: 460px;
    right: 0px !important;
    left: unset !important;
  }
  .highcharts-axis-line {
    display: none;
  }
`;

export default function HorizontalBarChart({
  data,
  size,
  onClick,
  prism,
  filterValues,
  colorScheme,
  chartTitle,
  chartSubtitle,
  chartYtitle,
  tooltipCfg,
  gridHeight,
  gridIndex,
  gridPrism,
  chartYmax,
  chartYmin,
}: any) {
  const chartRef = useRef<any>(null);

  const textBoldShadow = '0 0 .01px black, 0 0 .01px black, 0 0 .01px black';

  const [selectedLabel, setSelectedLabel] = useState(false);
  const [labelClicked, setLabelClicked] = useState(false);

  const exportFileName = buildChartExportFileName(chartTitle, filterValues, prism);

  useEffect(() => {
    const onScroll = () => {
      const elements = document.getElementsByClassName('xLabel');

      for (let i = 0; i < elements.length; i++) {
        (elements[i] as unknown as any).style.display = 'none';
      }
    };
    // clean up code
    window.removeEventListener('scroll', onScroll);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    function handleClick(e: any) {
      const file = (e.target as Element).getAttribute('download');
      if (file) {
        const fileType = file.substr(file.length - 3, 3);
        reactGA.event({ category: 'User', action: `Downloaded ${fileType}: ${file}` });
      }
    }
    window.addEventListener('click', handleClick);

    return function cleanupListener() {
      window.removeEventListener('click', handleClick);
    };
  }, [chartRef]);

  useEffect(() => {
    chartRef.current.reflow();
  }, [chartRef, size]);

  const chartOptions = useMemo(() => {
    const sortedData: IStackBarChart = cloneDeep({
      axisYLabels: data?.row_nodes?.map((r: any) => r.name),
      axisXLabels: data?.col_nodes?.map((c: any) => c.name),
      matrix: data?.data,
      data: data,
      values: [],
    });

    let maxValue = 0;

    const categoriesData: string[] = [];
    const seriesData: any[] = [];

    for (let j = 0; j < sortedData.axisXLabels?.length; ++j) {
      const seriesObj: ISeriesObj = {
        name: sortedData.axisXLabels[sortedData.axisXLabels?.length - j - 1],
        data: [],
        color: colorScheme ? colorScheme[sortedData.axisXLabels?.length - j - 1] : undefined,
      };
      for (let i = 0; i < sortedData.axisYLabels?.length; ++i) {
        if (j === 0) categoriesData.push(sortedData.axisYLabels[i]);
        const objData = data.data[i][sortedData.axisXLabels?.length - j - 1] as unknown as string;
        seriesObj.data.push(objData);
      }
      seriesData.push(seriesObj);
    }

    data?.data?.forEach((d: any) => {
      if (d[1] + d[0] > maxValue) maxValue = d[1] + d[0];
    });

    if (!data) return;
    return {
      chart: {
        className: prism.replace(' ', '') + gridIndex,
        width: size.width - 25,
        marginTop: chartSubtitle ? 85 : 70,
        height: (gridHeight / 4) * 446 + (gridHeight / 4 - 1) * 38 + 34,
        type: 'bar',
        style: {
          fontFamily: ['Montserrat', 'sans-serif'],
        },
      },
      exporting: {
        filename: exportFileName,
        csv: {
          columnHeaderFormatter: function (item: any) {
            if (item && item instanceof Highcharts.Axis) {
              if ((item as unknown as any).type === 'datetime') {
                return 'Date';
              }
              return 'Category';
            }
            return {
              columnTitle: item.name,
            };
          },
          dateFormat: '%m/%d/%Y',
        },
        enabled: true,
        buttons: {
          contextButton: {
            x: 0,
            y: 2,
            symbol: 'ellipsisvertical',
            menuClassName: 'highcharts-indicator-menu',
            menuItems: [
              'viewFullscreen',
              'downloadPNG',
              'downloadSVG',
              'downloadPDF',
              'separator',
              'downloadXLS',
              'downloadCSV',
            ],
          },
        },
        fallbackToExportServer: false,
      },
      title: {
        text: chartTitle,
        align: 'left',
        x: 12,
        y: 24,
        style: {
          fontSize: '18px',
          color: 'var(--color-chart-title)',
          fontWeight: 500,
          letterSpacing: '0.5px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
      },
      subtitle: {
        text: chartSubtitle,
        align: 'left',
        x: 12,
        y: 46,
        style: {
          color: 'var(--color-chart-title)',
          fontWeight: 'normal',
          letterSpacing: '0.5px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
      },
      credits: {
        enabled: false,
      },
      xAxis: {
        categories: categoriesData,
        gridZIndex: -1,
        zIndex: -1,
        labels: {
          formatter: function (): string {
            const obj = this as unknown as any;
            return `<div data-id='${`${prism.replace(' ', '')}${gridIndex}`}' id='${data.row_nodes[obj.pos].id}#${
              data.title
            }' data-name='${obj.value}' style='text-shadow: ${
              data?.row_nodes?.filter((c: any) => String(selectedLabel).includes(c.id))[0]?.name === obj.value &&
              prism === gridPrism
                ? textBoldShadow
                : 'none'
            }' class="clickableLabel">${obj.value.length > 20 ? `${obj.value.substring(0, 17)}...` : obj.value}</div>`;
          },
          events: {
            click: function (e: any) {
              const cellInfo = {
                source: 'value',
                topic: e.target.id.split('#')[0],
              };
              const chart = chartRef.current;
              if (chart.xAxisTooltip) {
                chart.xAxisTooltip.destroy();
                chart.xAxisTooltip = undefined;
              }

              const elements = document.getElementsByClassName('clickableLabel');

              for (let i = 0; i < elements.length; i++) {
                (elements[i] as unknown as any).style.textShadow = 'none';
              }

              setSelectedLabel(e.target.id);
              setLabelClicked(true);
              onClick(cellInfo);
            },
            mouseover: function () {
              if (labelClicked) return;
              const chartBox = document.querySelector(`.${prism.replace(' ', '')}${gridIndex}`);
              const chartRect = chartBox && chartBox.getBoundingClientRect();
              const obj = this as unknown as any;
              const value = obj.value.split("'")[3];
              const name = obj.value.split("'")[5];
              const objBox = document.querySelector(`[id="${value}"`);
              const objRect = objBox && objBox.getBoundingClientRect();
              const objOrg = data.row_nodes.filter((l: any) => l.name === value);
              const description = objOrg[0]?.description;
              const chart = chartRef.current;
              if (chart.xAxisTooltip !== undefined) chart.xAxisTooltip = undefined;
              const tooltipHtml = formatTooltipHtml({
                title: name,
                description,
                tooltipCfg,
                letterSpacingAdjustment: -0.75,
              });
              chart.xAxisTooltip = chart.renderer
                .label(tooltipHtml, 0, 0, 'rectangle', undefined, undefined, true)
                .css({
                  color: tooltipCfg.color,
                  width: tooltipCfg.maxWidth,
                  opacity: 0,
                  zIndex: 11111,
                })
                .attr({
                  className: 'tooltip',
                  class: 'tooltip xLabel',
                  fill: hex2rgba(tooltipCfg.backgroundColor, tooltipCfg.opacity),
                  stroke: tooltipCfg.borderColor,
                  zIndex: 11111,
                  'dominant-baseline': 'auto',
                  'stroke-width': tooltipCfg.borderWidth,
                  'stroke-dasharray':
                    tooltipCfg.borderStyle === 'solid' ? 'none' : tooltipCfg.borderStyle === 'dotted' ? '2,2' : '5,5',
                  'stroke-opacity': tooltipCfg.opacity,
                  padding: 8,
                  r: Number(tooltipCfg.borderRadius.replace('px', '')),
                })
                .add()
                .toFront();
              const tooltipRect = chart.xAxisTooltip.getBBox();
              const tooltipBorderWidth = Number(tooltipCfg.borderWidth.replace('px', ''));
              let tooltipX =
                objRect && objRect !== null && chartRect && chartRect !== null
                  ? objRect!.x + objRect!.width / 2 - chartRect!.x - tooltipRect.width / 2
                  : 0;
              if (tooltipX < tooltipBorderWidth) tooltipX = tooltipBorderWidth;
              if (tooltipX + tooltipRect.width > chartRect!.width)
                tooltipX = chartRect!.width - tooltipRect.width - tooltipBorderWidth;
              chart.xAxisTooltip
                .attr({
                  x: 120,
                  y:
                    objRect && objRect !== null && chartRect && chartRect !== null
                      ? objRect!.y - chartRect!.y - tooltipRect.height + 25
                      : 0,
                  zIndex: 11111,
                })
                .css({
                  opacity: 1,
                  zIndex: 11111,
                });
              const htmlObj = document.querySelector<HTMLElement>(`[data-id='${value}']`);
              if (htmlObj) htmlObj.style.textShadow = textBoldShadow;
            },
            mouseout: function () {
              const obj = this as unknown as any;
              if (labelClicked) return setLabelClicked(false);
              const chart = chartRef.current;
              if (chart.xAxisTooltip) {
                chart.xAxisTooltip.destroy();
              }
              const value = obj.value.split("'")[3];
              if (value === selectedLabel) return;
              const htmlObj = document.querySelector<HTMLElement>(`[data-id='${value}']`);
              if (htmlObj) htmlObj.style.textShadow = 'none';
            },
          },
          useHTML: true,
          style: {
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          },
        },
      },
      yAxis: {
        endOnTick: false,
        gridZIndex: -1,
        zIndex: -1,
        min: Number(chartYmin) || Number(0),
        max: Number(chartYmax) * 1.1 || Number(maxValue) * 1.1,
        title: {
          text: chartYtitle,
          y: -30,
          style: {
            color: 'var(--color-chart-title)',
            fontWeight: 'normal',
            letterSpacing: '0.5px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
        },
        stackLabels: {
          enabled: false,
        },
      },
      legend: {
        reversed: true,
        align: 'right',
        verticalAlign: 'top',
        x: -30,
        y: chartSubtitle ? -48 : -34,
        floating: false,
        backgroundColor: HighchartsReact.defaultProps?.defaultOptions?.legend.backgroundColor || 'white',
        shadow: false,
      },
      tooltip: {
        ...highchartsTooltipCfg(tooltipCfg),
        useHTML: true,
        shape: 'square',
        positioner: (labelWidth: any, _: any, point: any) => {
          return {
            x: 126,
            y: point.plotY + 15,
          };
        },
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        shared: false,
        style: {
          padding: '10px',
          width: '400px',
          fontSize: '14px',
          fontWeight: 'bold',
          fontStyle: 'normal',
          color: '#000000',
          zIndex: '19999',
        },
      },
      plotOptions: {
        series: {
          maxPointWidth: 30,
          cursor: 'pointer',
          point: {
            events: {
              click: (e: any) => {
                const cellInfo = {
                  source: 'value',
                  topic: data.row_nodes[e.point.index].id,
                };
                setSelectedLabel(data.row_nodes[e.point.index].id);
                setLabelClicked(true);
                onClick(cellInfo);
              },
            },
          },
        },
        column: {
          borderWidth: 0,
          stacking: 'normal',
          dataLabels: {
            enabled: false,
          },
        },
      },
      series: seriesData,
    };
  }, [
    data,
    size,
    exportFileName,
    onClick,
    chartRef,
    labelClicked,
    selectedLabel,
    colorScheme,
    tooltipCfg,
    chartTitle,
    chartSubtitle,
    chartYtitle,
    gridIndex,
    prism,
    gridPrism,
    chartYmax,
    chartYmin,
  ]);

  return (
    <Root $gridheight={gridHeight}>
      <HighchartContainer>
        <HighchartsReact
          containerProps={{ style: { height: '100%' } }}
          style={{ flex: 1 }}
          highcharts={Highcharts}
          constructorType={'chart'}
          options={chartOptions ?? EMPTY_OPTIONS}
          callback={(chart: any) => (chartRef.current = chart)}
        />
      </HighchartContainer>
    </Root>
  );
}
